import { type Dispatch, type SetStateAction, type SyntheticEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ParkingType, SortBy } from '../types'
import type { Garage, FiltersType, HoverPoint, SelectedPoint, Currency } from '../types'
import { useGetGarageList, useGetLaterAvailability } from '../pwa/hooks/use-search-api'
import { sortGarageList } from '../garage-list-sorting'

export const useGarageView = (currency: Currency, setTab: Dispatch<SetStateAction<0 | 2 | 1>>) => {
    const initialFilters: FiltersType = {
        minPrice: null,
        maxPrice: null,
        minHeight: null,
        minWidth: null,
        minLength: null,
        minWeight: null,
        parkingType: ParkingType.all,
        sort: SortBy.distance,
        electricAllowed: false,
        lpgCngAllowed: false,
        motorbikesAllowed: false,
        showAvailable: false,
    }

    const [currentFilters, setCurrentFilters] = useState<FiltersType>(initialFilters)

    const [desktopFilters, setDesktopFilters] = useState<boolean>(false)
    const [selectedId, setSelectedId] = useState<SelectedPoint | null>(null)
    const [hoverPoint, setHoverPoint] = useState<HoverPoint | null>(null)

    const { t, i18n } = useTranslation()
    const userLang = i18n.language

    const {
        searchLocationPoint,
        garageList,
        loadingGarageList,
        loadingGarageAvailability,
        availabilityErrors,
        priceRange,
        heightRange,
        widthRange,
        lengthRange,
        weightRange,
    } = useGetGarageList(userLang, currency)

    const { garagesAvailableLater, laterAvailabilityErrors } = useGetLaterAvailability(loadingGarageAvailability, garageList)

    const errors = useMemo(() => {
        const responseErrors = Array.from(new Set([...availabilityErrors, ...laterAvailabilityErrors]
            .filter(error => error.trim().length > 0)))
        const invalidGarages = garageList.filter(garage => garage.timeRangeValid === false)
        const garageErrors = Array.from(new Set(invalidGarages.flatMap(garage => garage.availabilityErrors)
            .filter(error => error.trim().length > 0)))
            .filter(error => !responseErrors.includes(error))

        return [
            ...responseErrors,
            ...(garageErrors.length > 0 && invalidGarages.length < garageList.length
                ? [t('general.someGaragesInvalidPeriod')]
                : []),
            ...garageErrors,
        ]
    }, [availabilityErrors, laterAvailabilityErrors, garageList, t])

    const filteredGarageList: Array<Garage> = garageList
        .filter(garage => !currentFilters.showAvailable || garage.available)
        .filter(garage => {
            const heightOk = !currentFilters.minHeight || !garage.height || garage.height >= currentFilters.minHeight
            const widthOk = !currentFilters.minWidth || !garage.width || garage.width >= currentFilters.minWidth
            const lengthOk = !currentFilters.minLength || !garage.length || garage.length >= currentFilters.minLength
            const weightOk = !currentFilters.minWeight || !garage.weight || garage.weight >= currentFilters.minWeight

            const parkingOk =
                currentFilters.parkingType === ParkingType.all ||
                (currentFilters.parkingType === ParkingType.parkingLot && garage.parkingLot) ||
                (currentFilters.parkingType === ParkingType.garage && !garage.parkingLot)

            const amountCents = currency === 'eur' ? garage.amountCentsEur : garage.amountCentsCzk
            const minPriceOk =
                !amountCents ||
                currentFilters.minPrice === null ||
                amountCents / 100 >= currentFilters.minPrice
            const maxPriceOk =
                !amountCents ||
                currentFilters.maxPrice === null ||
                amountCents / 100 <= currentFilters.maxPrice
            
            const electricOk = !currentFilters.electricAllowed || !garage.electricCarNotAllowed
            const lpgCngOk = !currentFilters.lpgCngAllowed || !garage.lpgCngNotAllowed
            const motorbikesOk = !currentFilters.motorbikesAllowed || !garage.motorbikesNotAllowed

            return (
                heightOk &&
                widthOk &&
                lengthOk &&
                weightOk &&
                parkingOk &&
                minPriceOk &&
                maxPriceOk &&
                electricOk &&
                lpgCngOk &&
                motorbikesOk
            )
        })

    const laterAvailableGarageIds = new Set(
        garagesAvailableLater?.garages.map(garage => garage.garageId) ?? [],
    )
    const sortedFilteredGarageList = sortGarageList(
        filteredGarageList,
        currentFilters.sort,
        laterAvailableGarageIds,
    )

    /**
     * Resets the filters
     */
    const handleFiltersReset = () => {
        setCurrentFilters(initialFilters)
        setTab(0)
    }

    const handleTabChange = (
        _event: SyntheticEvent<Element, Event> | null,
        newValue: string | number | null
    ) => {
        setTab(newValue as 0 | 1 | 2)
    }

    const handleToggleDesktopFilters = () => {
        setDesktopFilters(!desktopFilters)
    }

    function shallowEqual(currentFilters: FiltersType, initialFilters: FiltersType) {
        const keys = Object.keys(currentFilters) as (keyof FiltersType)[]

        if (keys.length !== Object.keys(initialFilters).length) {
            return keys.length - Object.keys(initialFilters).length
        }

        const overrides: Partial<Record<keyof FiltersType, unknown>> = {
            minPrice: priceRange?.min,
            maxPrice: priceRange?.max,
            minHeight: heightRange?.min,
            minWidth: widthRange?.min,
            minLength: lengthRange?.min,
            minWeight: weightRange?.min,
        }

        let difference = 0

        for (const key of keys) {
            const current = currentFilters[key]
            const compareTo =
                key in overrides ? overrides[key] : initialFilters[key]

            if (current !== null && current !== compareTo) {
                difference++
            }
        }

        return difference
    }

    return {
        initialFilters,
        currentFilters,
        setCurrentFilters,
        priceRange,
        heightRange,
        widthRange,
        lengthRange,
        weightRange,
        desktopFilters,
        selectedId,
        setSelectedId,
        hoverPoint,
        setHoverPoint,
        searchLocationPoint,
        sortedFilteredGarageList,
        loadingGarageList,
        loadingGarageAvailability,
        garagesAvailableLater,
        errors,
        handleFiltersReset,
        handleTabChange,
        handleToggleDesktopFilters,
        shallowEqual,
    }
}
